// vendors
import getDistance from 'geolib/es/getDistance'
import isValidCoordinate from 'geolib/es/isValidCoordinate'
import _get from 'lodash/get'
import _some from 'lodash/some'
import _omit from 'lodash/omit'
import _isArray from 'lodash/isArray'
import _isPlainObject from 'lodash/isPlainObject'
import _mapValues from 'lodash/mapValues'
import _omitBy from 'lodash/omitBy'
import _isNil from 'lodash/isNil'
import _flatMap from 'lodash/flatMap'
import _isEmpty from 'lodash/isEmpty'
import _uniq from 'lodash/uniq'
import _flatten from 'lodash/flatten'
import _max from 'lodash/max'
import _minBy from 'lodash/minBy'
import _memoize from 'lodash/memoize'
import _isEqual from 'lodash/isEqual'
import _set from 'lodash/set'
import _merge from 'lodash/merge'
import {firestore, firestoreConstructors} from '../plugins/firebase'
import {store} from '../store'


// constants
import { EQUIPMENT_TYPE, DEFAULT_DAYS_BEFORE_START_TO_ALLOW_INITIAL_DATA, DEVICE_CONSTANTS, AUDITS } from '../constants/const'

/**
 * Sorting function that campares to objects with a name property, the names are compared alphabetically.
 * This function is used in a sort function of an array.
 * @param {{name: string}} a
 * @param {{name: string}} b
 * @returns {number}
 */
export function sortByName (a, b) {
  const aName = a.name.toUpperCase()
  const bName = b.name.toUpperCase()
  return aName.localeCompare(bName, undefined, {numeric: true, sensitivity: 'base'})
}

/**
 * It converts a string date in ISO format to `day/month/year` format,
 * without modifying the original string.
 * @param {string} isoDate string date in ISO format.
 * @returns {string}
 */
export function isoDateToSlashes (isoDate) {
  if (!isoDate) return ''

  const [year, month, day] = isoDate.split('T')[0].split('-')

  return `${day}/${month}/${year}`
}

/**
 * Checks if a value exists into an object, if it exists it is returned as a float number
 * @param {Object} obj
 * @param {string} path
 * @returns {number|null}
 */
export function checkAndParseFloat (obj, path) {
  const val = _get(obj, path)
  return val ? parseFloat(val) : null
}

/**
 * Parses the device data retrieved from Firestore into the required format for this app
 * @param {string} deviceId
 * @param {Object} deviceData - Raw data from Firebase
 * @returns {Object}
 */
export function parseDevice (deviceId, deviceData) {
  let notifications = []
  deviceData.users.data.forEach(user => {
    notifications[user.id] = {
      id: user.id,
      name: user.name,
      permissions: {
        failure: false,
        pressure: false,
        off: false,
        position: false
      }
    }
  })
  Object.entries(deviceData.users.permissions.notifications).forEach((permission) => {
    permission[1].forEach(user => {
      if (notifications[user.id]) {
        notifications[user.id].permissions[permission[0]] = true
      }
    })
  })

  // Device crops parsing
  let campaign = deviceData.campaign
  campaign = {
    ...campaign,
    crops: campaign.crops.map(crop => {
      // Map only the fields we need, avoiding Firestore internal fields
      const mappedCrop = {
        name: crop.name || '',
        device: deviceId,
        originalDevice: deviceId,
        field: crop.field && crop.field.id,
        startDate: crop.startDate || '',
        radius: crop.radius || 0, // radius doesn't exist in pivot lots created before the CropEditor form was submitted to production
        cropRef: crop.cropRef && crop.cropRef.id,
        clonedFrom: crop.clonedFrom && crop.clonedFrom.length > 0 ? crop.clonedFrom.map(ref => ref.id) : [],
        organization: crop.organization && crop.organization.id,
        campaign: crop.campaign && crop.campaign.id
      }

      // Handle GeoPoint fields if they exist
      Object.entries(crop).forEach(([key, value]) => {
        if (value instanceof firestoreConstructors.GeoPoint) {
          mappedCrop[key] = {latitude: value.latitude, longitude: value.longitude}
        }
      })

      return mappedCrop
    })
  }

  let pluviometricCharts = deviceData.pluviometricCharts
  if (pluviometricCharts && pluviometricCharts.length) {
    pluviometricCharts = pluviometricCharts.map(chart => {
      const chartObj = {
        ...chart,
        parsedChartId: chart.parsedChartReference ? chart.parsedChartReference.id : ''
      }
      delete chartObj.parsedChartReference

      return chartObj
    })
  }

  let postponementRequest = deviceData.postponementRequest
  if (postponementRequest) {
    postponementRequest = {
      ...postponementRequest,
      suggestedDate: postponementRequest.suggestedDate.toDate()
    }
  }

  return {
    id: deviceId,
    currentCampaign: deviceData.currentCampaign && deviceData.currentCampaign.id,
    campaign,
    equipment: deviceData.equipment,
    pluviometricCharts,
    users: deviceData.users && deviceData.users.data ? deviceData.users.data.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)) : [],
    notifications: notifications ? Object.values(notifications) : [],
    isDemoDevice: deviceData.isDemoDevice,
    clonedFrom: deviceData.clonedFrom ? deviceData.clonedFrom.map(cloned => cloned.id) : [],
    hasCompleteData: !!deviceData.hasCompleteData,
    zoom: deviceData.zoom,
    postponementRequest,
    serviceStartDate: deviceData.serviceStartDate && deviceData.serviceStartDate.toDate(),
    serviceEndDate: deviceData.serviceEndDate && deviceData.serviceEndDate.toDate(),
    serviceExtensionRequests: deviceData.serviceExtensionRequests || [],
    installationField: deviceData.installationField && deviceData.installationField.id,
    installationCoordinates: deviceData.installationCoordinates ? parseFirestoreLocation(deviceData.installationCoordinates) : null,
    modem: deviceData.modem || {}
  }
}

function toDegrees (radians) {
  return radians * 180 / Math.PI
}
function toRadians (degrees) {
  return degrees * Math.PI / 180
}

/**
 * Calculates the angle that the "destiny Point" has with regard to the "start point"
 * @param {{latitude: number, longitude: number}} startCoord
 * @param {{latitude: number, longitude: number}} destCoord
 * @returns {number}
 */
export function getBearing (startCoord, destCoord) {
  const startLat = toRadians(startCoord.latitude)
  const startLng = toRadians(startCoord.longitude)
  const destLat = toRadians(destCoord.latitude)
  const destLng = toRadians(destCoord.longitude)

  let y = Math.sin(destLng - startLng) * Math.cos(destLat)
  let x = Math.cos(startLat) * Math.sin(destLat) -
        Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng)
  let brng = Math.atan2(y, x)
  brng = toDegrees(brng)
  return (brng + 360) % 360
}

/**
 * Check if an user is authorized to manage the organization data
 * @param {{roles: Object, editDataPermission: boolean}} user - The stored user object
 * @param {string|Boolean} impersonating - Whether the user is being impersonated
 * @returns {boolean}
 */
export function isAuthToEdit (user, impersonating = false) {
  const hasEditPermission = _some(user.userCampaignsSettings, campaign => campaign.editDataPermission)
  return impersonating || (user && (user.isAdmin || hasEditPermission))
}

/**
 * Check if an user is authorized to view reports data
 * @param {{roles: Object, reports: boolean}} user - The stored user object
 * @param {string|Boolean} impersonating - Whether the user is being impersonated
 * @returns {boolean}
 */
export function isAuthToReports (user, impersonating = false) {
  const hasReportsPermission = _some(user.userCampaignsSettings, campaign => campaign.reports)
  return impersonating || (user && (user.isAdmin || hasReportsPermission))
}

/**
 * Check if an user is authorized to view audits data
 * @param {{roles: Object, audits: boolean}} user - The stored user object
 * @param {string|Boolean} impersonating - Whether the user is being impersonated
 * @returns {boolean}
 */
export function isAuthToAudits (user, impersonating = false) {
  return impersonating || (user && (user.isAdmin || user.audits || (user.fieldSurveyIds && user.fieldSurveyIds.length > 0)))
}

/**
 * Field survey entry route: audits users, admins, or users with fieldSurveyIds on their profile.
 * @param {{ isAdmin?: boolean, audits?: boolean, fieldSurveyIds?: string[] }} user
 * @param {string|boolean} impersonating
 * @returns {boolean}
 */
export function isAuthToFieldSurveys (user, impersonating = false) {
  if (impersonating) return true
  if (!user) return false
  if (user.isAdmin || user.audits) return true
  return !!(user.fieldSurveyIds && user.fieldSurveyIds.length)
}

/**
 * Parsed the pluviemetric chart data retrieved from firestore into values that are shown in the edit cards
 * @param {{linkToFile: string, validityDate: Date, creationDate: Date, parsedChartId: string}} pluviometricChart - Data from firestore
 * @returns {pluviometricChartFile: File, validityDate: string, creationDate: string, parsedChartId: string}
 */
export function parsePluviometricChartData (pluviometricChart) {
  let pluviometricChartFile
  let validityDate = ''
  let creationDate = ''
  let parsedChartId = ''

  if (pluviometricChart) {
    pluviometricChartFile = pluviometricChart.linkToFile
      // creates empty file so the v-file-input shows the file name
      ? new File([], pluviometricChart.linkToFile.split('/').pop())
      : undefined
    validityDate = pluviometricChart.validityDate ? pluviometricChart.validityDate.toDate().toISOString() : ''
    creationDate = pluviometricChart.creationDate ? pluviometricChart.creationDate.toDate().toISOString() : ''
    parsedChartId = pluviometricChart.parsedChartId || ''
  }

  return {pluviometricChartFile, validityDate, creationDate, parsedChartId}
}

/**
 * It gets the zoom for a Pivot, depending on its length.
 * @param {number} length
 * @returns {number} the zoom for a pivot
 */
export function getPivotZoom (length = 0) {
  if (length < 310) return 16
  if (length < 650) return 15
  return 14
}

/**
 * It gets the zoom for a Linear or Irrigation Reel, depending on its length.
 * @param {number} length
 * @returns {number} the zoom for a Linear or Irrigation Reel
 */
function getLinearOrReelZoom (length = 0) {
  if (length < 2800) return 15
  if (length < 3500) return 14
  if (length < 9000) return 13
  return 12
}

/**
 * It gets the max lot distance of an Irrigation Reel.
 * @param {Array<{ points: Array<{ latitude: number, longitude: number }> }>} lots
 * the device's lots
 * @returns {number} the max lot distance
 */
function getIRLotMaxDistance (lots = []) {
  let maxDistance = 0

  lots.forEach(({ points }) => {
    if (points) {
      let maxLat, minLat, maxLon, minLon

      points.forEach(({ latitude, longitude }) => {
        if (!minLat || latitude < minLat) minLat = latitude
        if (!maxLat || latitude > maxLat) maxLat = latitude
        if (!minLon || longitude < minLon) minLon = longitude
        if (!maxLon || longitude > maxLon) maxLon = longitude
      })

      const latitudeDistance = getDistance({ latitude: minLat, longitude: 0 }, { latitude: maxLat, longitude: 0 }, 0.01)
      const longitudeDistance = getDistance({ latitude: 0, longitude: minLon }, { latitude: 0, longitude: maxLon }, 0.01)
      const distance = latitudeDistance > longitudeDistance ? latitudeDistance : longitudeDistance

      if (!maxDistance || distance > maxDistance) maxDistance = distance
    }
  })

  return maxDistance
}

/**
 * It calculates the map zoom for a device, depending on its type and length or
 * lots' length.
 * @param {EQUIPMENT_TYPE} type device type
 * @param {number} length device length
 * device lots, to be used if the device is an Irrigation Reel
 * @returns {number} the map zoom to be used for the device
 */
export function getZoom (type, length = 0) {
  switch (type) {
    case EQUIPMENT_TYPE.PIVOT:
      return getPivotZoom(length)
    case EQUIPMENT_TYPE.AF:
      return getLinearOrReelZoom(length)
    case EQUIPMENT_TYPE.IR:
      return getLinearOrReelZoom(getIRLotMaxDistance())
    default:
      return 0
  }
}

export async function makeFetchQueryRequest (body, user) {
  const fetchOptions = {
    method: 'POST',
    headers: {
      'auth-token': user ? await user.getIdToken() : ''
    },
    body: JSON.stringify(body)
  }
  const res = await fetch(`https://us-central1-${process.env.VUE_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/fetchQuery`, fetchOptions)
  return res.json()
}

export function parseFirestoreLocation (location) {
  return {
    latitude: location.latitude,
    longitude: location.longitude
  }
}

export function getCoordinate (coordinate) {
  let result = {
    latitude: 0,
    longitude: 0
  }
  if (coordinate.latitude && coordinate.longitude) {
    result = {
      latitude: coordinate.latitude,
      longitude: coordinate.longitude
    }
  } else if (coordinate._latitude && coordinate._longitude) {
    result = {
      latitude: coordinate._latitude,
      longitude: coordinate._longitude
    }
  } else if (coordinate.getLatitude && coordinate.getLongitude) {
    result = {
      latitude: coordinate.getLatitude(),
      longitude: coordinate.getLongitude()
    }
  }
  return result
}

export function parsePhoneNumberForWaMe (phone) {
  return phone.replaceAll(/\s/g, '').replaceAll('+', '').replaceAll('-', '')
}

/**
 * Recursively removes null and undefined values from objects and arrays.
 * @param {*} value - Any value (object, array, primitive).
 * @returns {*} - Cleaned value with null/undefined removed.
 */
export function cleanDeep(value) {
  if (_isArray(value)) {
    return value
      .map(cleanDeep)
      .filter(v => !_isNil(v))
  }

  if (_isPlainObject(value)) {
    const mapped = _mapValues(value, cleanDeep)
    return _omitBy(mapped, v => _isNil(v))
  }

  return value
}

/**
 * Converts a given index to its corresponding uppercase letter in the English alphabet.
 *
 * @param {number} index - The index to convert, where 1 corresponds to 'A', 2 to 'B', etc.
 * @returns {string} The uppercase letter corresponding to the given index.
 */
export function getLetterFromIndex (index) {
  return String.fromCharCode(64 + index)
}

/**
 * Gets campaign IDs with pending initial data that the user has permission to edit.
 * Filters campaigns based on user permissions and date range (X days before estimated start date).
 * Returns array of campaign IDs sorted chronologically by estimatedStartDate.
 *
 * @param {Array<object>} campaigns - Array of campaign objects
 * @param {Array<object>} userCampaignsSettings - User's campaign permissions
 * @returns {Array<string>} Array of campaign IDs with pending initial data, sorted by estimatedStartDate
 */
export function getCampaignIdsWithPendingInitialData (campaigns, userCampaignsSettings) {
  // Get campaign IDs where user has owner role or edit permission
  const filteredCampaignsIds = userCampaignsSettings
    .filter(userCampaign => userCampaign.roles.owner || userCampaign.editCampaignDataPermission)
    .map(campaign => campaign.id)

  // Filter campaigns by user permissions and date range (90 days before estimated start date)
  const filteredCampaigns = campaigns.filter(campaign =>
    filteredCampaignsIds.includes(campaign.id) &&
    (new Date() >= (campaign.estimatedStartDate.toDate() - 1000 * 60 * 60 * 24 * ((store.getters.globalConstants && store.getters.globalConstants.daysBeforeStartToAllowInitialData) || DEFAULT_DAYS_BEFORE_START_TO_ALLOW_INITIAL_DATA))) &&
    // Validate that at least one property in preDataSteps is false (has pending initial data)
    campaign.preDataSteps && Object.values(campaign.preDataSteps).some(step => step === false)
  )

  // Return empty array if no campaigns match the criteria
  if (filteredCampaigns.length === 0) {
    return []
  }

  // Sort campaigns chronologically by estimatedStartDate (ascending order) and return IDs
  const sortedCampaignIds = filteredCampaigns
    .sort((a, b) => a.estimatedStartDate.toDate() - b.estimatedStartDate.toDate())
    .map(campaign => campaign.id)

  return sortedCampaignIds
}

/**
 * Checks if a user has active campaigns based on campaign date ranges.
 * A campaign is considered active if the current date falls between campaign.fromDate and campaign.toDate.
 *
 * @param {Array<object>} campaigns - Array of campaign objects
 * @param {Array<object>} userCampaignsSettings - User's campaign permissions
 * @returns {boolean} True if user has at least one active campaign, false otherwise
 */
export function hasUserActiveCampaigns (campaigns, userCampaignsSettings) {
  // Get campaign IDs where user has any role or permission
  const filteredCampaignsIds = userCampaignsSettings
    .filter(userCampaign => userCampaign.roles.owner || userCampaign.editCampaignDataPermission)
    .map(campaign => campaign.id)

  // Check if any campaign is currently active (current date is between fromDate and toDate)
  const hasActiveCampaign = campaigns.some(campaign => {
    // Check if user has access to this campaign
    if (!filteredCampaignsIds.includes(campaign.id)) {
      return false
    }

    // Check if campaign has valid date range
    if (!campaign.fromDate || !campaign.toDate) {
      return false
    }

    const currentDate = new Date()
    const fromDate = campaign.fromDate.toDate ? campaign.fromDate.toDate() : new Date(campaign.fromDate)
    const toDate = campaign.toDate.toDate ? campaign.toDate.toDate() : new Date(campaign.toDate)

    // Campaign is active if current date is between fromDate and toDate (inclusive)
    return currentDate >= fromDate && currentDate <= toDate
  })

  return hasActiveCampaign
}

function dmsComponentToDecimal (degrees, minutes, seconds, direction) {
  let decimal = Math.abs(degrees) + (minutes / 60) + (seconds / 3600)
  if (/[SW]/i.test(direction)) decimal *= -1
  return decimal
}

function parseDMSPart (part) {
  // First try DDM format: 33°07.406' S
  const ddmRegex = /(\d{1,3})[°]\s*(\d{1,2}(?:\.\d+)?)[']\s*([NSEW])/i
  const ddmMatch = part.trim().match(ddmRegex)

  if (ddmMatch) {
    const [, deg, min, dir] = ddmMatch
    return dmsComponentToDecimal(parseFloat(deg), parseFloat(min), 0, dir)
  }

  // Then try DMS format: 33°07'24.4"S
  const dmsRegex = /(\d{1,3})[°]\s*(\d{1,2})?[']\s*(\d{1,2}(?:\.\d+)?)?["]\s*([NSEW])/i
  const dmsMatch = part.trim().match(dmsRegex)

  if (!dmsMatch) return null

  const [, deg, min = 0, sec = 0, dir] = dmsMatch
  return dmsComponentToDecimal(parseFloat(deg), parseFloat(min), parseFloat(sec), dir)
}

function isLikelyDMS (input) {
  return /[°′'″"]/g.test(input) || /[NSEW]/i.test(input)
}

function parseDecimalLatLonString (input) {
  const parts = input.split(/[, ]+/).map(s => s.trim()).filter(Boolean)
  if (parts.length !== 2) return null

  const lat = parseFloat(parts[0])
  const lon = parseFloat(parts[1])
  if (isNaN(lat) || isNaN(lon)) return null

  return { latitude: lat, longitude: lon }
}

function parseDMSLatLonString (input) {
  // Look for patterns like: 33°07.406' S, 60°59.259' W or 33°07.406' S 60°59.259' W
  // But NOT: 33°07.406' S60°59.259' W (missing separator)
  const dmsPattern = /^\s*(\d{1,3}°\s*\d{1,2}(?:\.\d+)?'\s*(?:\d{1,2}(?:\.\d+)?")?\s*[NS])\s*(?:,\s*|\s+)(\d{1,3}°\s*\d{1,2}(?:\.\d+)?'\s*(?:\d{1,2}(?:\.\d+)?")?\s*[EW])\s*$/i
  const match = input.match(dmsPattern)

  if (!match) return null

  const lat = parseDMSPart(match[1])
  const lon = parseDMSPart(match[2])
  if (lat === null || lon === null) return null

  return { latitude: lat, longitude: lon }
}

export function validateLatLon (input) {
  if (!input || typeof input !== 'string') {
    return false
  }

  const trimmedInput = input.trim()
  if (!trimmedInput) {
    return false
  }

  const coords = isLikelyDMS(trimmedInput)
    ? parseDMSLatLonString(trimmedInput)
    : parseDecimalLatLonString(trimmedInput)

  if (!coords) {
    return false
  }

  return isValidCoordinate(coords)
}

export function replaceWithBr (s) {
  if (s) {
    return s.replace(/\n/g, '<br />')
  } else {
    return ''
  }
}

export function getUserImg (user) {
  if (user.photoUrl) {
    return user.profilePic
  }
  switch (user.role) {
    case 'admin':
    case 'owner':
      return require('../assets/owner.png')
    case 'manager':
      return require('../assets/manager.png')
    case 'fieldman':
      return require('../assets/fieldman.png')
    case 'farmhand':
      return require('../assets/farmhand.png')
    default:
      return require('../assets/owner.png')
  }
}

export function parseCampaignData (doc) {
  const campaignData = doc.data()

  // Handle device references if they exist
  let processedData = { ...campaignData }

  // If campaign has devices array, convert references to IDs
  if (campaignData.devices && Array.isArray(campaignData.devices)) {
    processedData.devices = campaignData.devices.map(device => {
      // If device is a Firestore reference, extract the ID
      if (device && typeof device === 'object' && device.id) {
        return device.id
      }
      // If device is already an ID string, return as is
      return device
    })
  }

  // If campaign has fields array, convert references to IDs
  if (campaignData.fields && Array.isArray(campaignData.fields)) {
    processedData.fields = campaignData.fields.map(field => {
      // If field is a Firestore reference, extract the ID
      if (field && typeof field === 'object' && field.id) {
        return field.id
      }
      // If field is already an ID string, return as is
      return field
    })
  }

  // If campaign has product array with devices, handle those too
  if (campaignData.product && Array.isArray(campaignData.product)) {
    processedData.product = campaignData.product.map(product => {
      if (product.devices && Array.isArray(product.devices)) {
        return {
          ...product,
          devices: product.devices.map(device => {
            if (device && typeof device === 'object' && device.device && device.device.id) {
              return {
                ...device,
                device: device.device.id
              }
            }
            return device
          })
        }
      }
      return product
    })
  }

  if(campaignData.users && Array.isArray(campaignData.users)) {
    processedData.users = campaignData.users.map(user => {
      if (user && typeof user === 'object' && user.id) {
        return user.id
      }
      return user
    })
  }

  if(campaignData.organization && typeof campaignData.organization === 'object' && campaignData.organization.id) {
    processedData.organization = campaignData.organization.id
  }

  // Handle preDataProductsDetail references
  if (campaignData.preDataProductsDetail && Array.isArray(campaignData.preDataProductsDetail)) {
    processedData.preDataProductsDetail = campaignData.preDataProductsDetail.map(product => {
      if (product.devices && Array.isArray(product.devices)) {
        return {
          ...product,
          devices: product.devices.map(device => {
            const processedDevice = { ...device }

            // Convert device reference to ID
            if (device.device && typeof device.device === 'object' && device.device.id) {
              processedDevice.device = device.device.id
            }

            // Convert field reference to ID
            if (device.field && typeof device.field === 'object' && device.field.id) {
              processedDevice.field = device.field.id
            }

            // Convert Firestore timestamps to date strings
            if (device.startDate && typeof device.startDate === 'object' && device.startDate.toDate) {
              const date = device.startDate.toDate()
              processedDevice.startDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`
            }

            if (device.endDate && typeof device.endDate === 'object' && device.endDate.toDate) {
              const date = device.endDate.toDate()
              processedDevice.endDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`
            }

            return processedDevice
          })
        }
      }
      return product
    })
  }

  return {
    id: doc.id,
    ..._omit(processedData, ['termsAndConditionsAcceptance.user'])
  }

}

/**
 * Checks if a device is near the end of its service period.
 * A device is considered "near end of service" if it's within the configured
 * number of days before the service end date.
 *
 * @param {Object} device - The device object containing serviceEndDate
 * @param {Date} device.serviceEndDate - The service end date
 * @param {Object} globalConstants - Global constants object containing daysForEndOfServiceNotice
 * @param {number} globalConstants.daysForEndOfServiceNotice - Number of days before end to show notice (default: 30)
 * @returns {boolean} True if device is near end of service, false otherwise
 */
export function isNearEndOfService (device, globalConstants = {}) {
  if (!device.serviceEndDate) return false

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const endDate = new Date(device.serviceEndDate)
  endDate.setHours(0, 0, 0, 0)

  const daysUntilEnd = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))

  // Get the constant from global constants, default to 30 days if not found
  const daysForEndOfServiceNotice = globalConstants.daysForEndOfServiceNotice || DEVICE_CONSTANTS.DAYS_FOR_END_OF_SERVICE_NOTICE

  return daysUntilEnd <= daysForEndOfServiceNotice && daysUntilEnd > 0
}

/**
 * Filters and returns only the latest cloned devices from a list of devices.
 * Each device contains a `name` (its unique ID) and a `clonedFrom` array
 * indicating the chain of devices it was cloned from.
 *
 * The logic is: a device that has been cloned will appear in another device's
 * `clonedFrom` list. Therefore, the latest cloned devices are those whose `name`
 * does not appear in any other `clonedFrom` array.
 *
 * @param {Array<{ name: string, clonedFrom: string[] }>} devices - The list of all devices.
 * @returns {Array<{ name: string, clonedFrom: string[] }>} - Only the latest cloned devices.
 */
export function getLatestClonedDevices(devices) {
  // Collect all device names that appear in any clonedFrom array
  const allClonedIds = new Set(_flatMap(devices, device => device.clonedFrom))

  // Filter devices whose name is NOT included in the clonedFrom sets
  // These are considered the latest in their respective cloning chains
  return devices.filter(device => !allClonedIds.has(device.id))
}

/**
 * Parses the field data retrieved from Firestore into the required format for this app
 * @param {string} fieldId
 * @param {Object} fieldData - Raw data from Firebase
 * @returns {Object}
 */
export function parseField(fieldId, fieldData) {
  let crops = []
  if (fieldData.crops) {
    crops = fieldData.crops.map(crop => crop.id)
  }

  // Create editable copies of gates to avoid readonly property issues
  let gates = []
  if (fieldData.gates) {
    gates = fieldData.gates.map(gate => ({
      latitude: gate.latitude || '',
      longitude: gate.longitude || ''
    }))
  }

  return {
    id: fieldId,
    name: fieldData.name,
    organization: fieldData.organization && fieldData.organization.id,
    contact: fieldData.contact && fieldData.contact.id,
    auxContact: fieldData.auxContact && fieldData.auxContact.id,
    gates: gates,
    crops: crops,
    clonedFrom: fieldData.clonedFrom ? fieldData.clonedFrom.map(cloned => cloned.id) : []
  }
}

/**
 * Filters and returns only the latest cloned fields from a list of fields.
 * Each field contains a `name` (its unique ID) and a `clonedFrom` array
 * indicating the chain of fields it was cloned from.
 *
 * The logic is: a field that has been cloned will appear in another field's
 * `clonedFrom` list. Therefore, the latest cloned fields are those whose `name`
 * does not appear in any other `clonedFrom` array.
 *
 * @param {Array<{ name: string, clonedFrom: string[] }>} fields - The list of all fields.
 * @returns {Array<{ name: string, clonedFrom: string[] }>} - Only the latest cloned fields.
 */
export function getLatestClonedFields(fields) {
  // Collect all field names that appear in any clonedFrom array
  const allClonedIds = new Set(_flatMap(fields, field => field.clonedFrom || []))

  // Filter fields whose name is NOT included in the clonedFrom sets
  // These are considered the latest in their respective cloning chains
  return fields.filter(field => !allClonedIds.has(field.id))
}

/**
 * Gets crops data for a list of fields from Firestore.
 *
 * @param {Array} fields - Array of field objects with crops array
 * @param {Object} db - Firestore database instance
 * @returns {Promise<Array>} Array of processed crop data
 */
export async function getCropsForFields(fields, db) {

  const cropsIds = _uniq(_flatten(fields.map(field => field.crops)))
  const cropsQueries = []
  const cropsData = []

  while (cropsIds.length) {
    const cropsIdsBatch = cropsIds.splice(0, 10)
    const cropsQuery = db.collection('app/v1/crops').where(firestoreConstructors.FieldPath.documentId(), 'in', cropsIdsBatch).get()
    cropsQueries.push(cropsQuery)
  }

  const cropsQueriesSnapshot = await Promise.all(cropsQueries)
  const crops = cropsQueriesSnapshot.reduce((cropsAcc, snapshot) => {
    snapshot.forEach(doc => { cropsAcc.push(doc) })
    return cropsAcc
  }, [])

  crops.forEach(crop => {
    if (crop.data()) {
      const cropData = crop.data()
      // Skip crops that have been logically deleted
      if ((!cropData.devices || cropData.devices.length === 0) && !cropData.field) {
        return
      }

      const startDate = cropData.startDate ? typeof cropData.startDate === 'string' ? new Date(cropData.startDate) : cropData.startDate.toDate() : undefined
      const devices = cropData.devices ? cropData.devices.map(device => device.id) : []
      const campaign = cropData.campaign && cropData.campaign.id
      const organization = cropData.organization && cropData.organization.id
      const cropDataObj = {
        ...cropData,
        id: crop.id,
        field: cropData.field && cropData.field.id,
        devices,
        campaign,
        organization,
        clonedFrom: cropData.clonedFrom && cropData.clonedFrom.id,
        type: cropData.type.split('|'),
        sowingDate: startDate ? `${startDate.getDate().toString().padStart(2, '0')}/${(startDate.getMonth() + 1).toString().padStart(2, '0')}/${startDate.getFullYear()}` : ''
      }
      if (cropDataObj.location) {
        cropDataObj.location = parseFirestoreLocation(cropDataObj.location)
      }
      if (cropDataObj.startPoint) {
        cropDataObj.startPoint = parseFirestoreLocation(cropDataObj.startPoint)
      }
      if (cropDataObj.endPoint) {
        cropDataObj.endPoint = parseFirestoreLocation(cropDataObj.endPoint)
      }
      cropsData.push(cropDataObj)
    }
  })
  return cropsData
}

/**
 * Loads campaign data into the store including devices, fields, users, and crops.
 * This function can be used when a campaign is selected or when a new campaign is finished.
 *
 * @param {string} campaignId - The ID of the campaign to load
 * @returns {Promise<void>}
 */
export async function loadCampaignDataToStore(campaignId) {
  try {
    const user = store.getters.user
    if (!user || !user.campaigns) {
      console.warn('User or campaigns not available')
      return
    }

    // Find the selected campaign
    const selectedCampaign = user.campaigns.find(campaign => campaign.id === campaignId)
    if (!selectedCampaign) {
      console.warn('Selected campaign not found:', campaignId)
      return
    }

    // Update devices and fields in store
    const devices = selectedCampaign.devices || []
    const fields = selectedCampaign.fields || []
    const users = selectedCampaign.users || []

    store.commit('setDevices', devices)
    store.commit('setFields', fields)
    store.commit('setUsers', users)

    // Load crops for the new fields
    if (fields.length > 0) {
      const db = await firestore
      const crops = await getCropsForFields(fields, db)
      store.commit('setCrops', crops)
    }

  } catch (error) {
    console.error('Error loading campaign data:', error)
    throw error
  }
}

export function ddMMyyyyToJsDate (date) {
  const [day, month, year] = date.split('/')
  return new Date(year, month - 1, day)
}

export async function getSatisfactionSurveyConstants () {
  if (_isEmpty(_get(store, 'getters.satisfactionSurveyConstants', {}))) {
    const db = await firestore
    const satisfactionSurveyConstants = await db.collection('app/v1/constants').doc('satisfactionSurvey').get()
    store.commit('setSatisfactionSurveyConstants', satisfactionSurveyConstants.data() || {})
  }
  return _get(store, 'getters.satisfactionSurveyConstants', {})
}

/**
 * Calculates the percentage of correct outlets configuration for an audit.
 *
 * @param {Object} auditData - The audit data object containing the spans and outlets.
 * @returns {Object} - The percentage of correct outlets configuration, the total number of outlets, and the number of correct outlets.
 */
export function getOutletsConfigurationAnalysis (auditData) {
  let total = 0
  let correct = 0

  let outletsWithoutRegulator = []
  let sprinklersWithoutNozzle = []
  let sprinklersWithoutPad = []

  if (auditData.spans && auditData.spans.length > 0) {
    auditData.spans.forEach(span => {
      if (span.outlets && span.outlets.length > 0) {
        span.outlets.forEach(outlet => {
          if (!outlet.type || outlet.type.id !== AUDITS.OUTLET_TYPES.OUTLET.ID) {
            // If an outlet is plugged, it's not considered to calculate the percentage
            return
          }
          if (outlet.sprinkler && outlet.sprinkler.nozzle && outlet.sprinkler.pad && outlet.regulator) {
            correct++
          } else {
            if (!outlet.regulator) {
              outletsWithoutRegulator.push({
                outletNumber: outlet.number,
                spanNumber: span.number
              })
            }

            if (outlet.sprinkler && ![AUDITS.SPRINKLER_TYPES.WITHOUT_SPRINKLER, AUDITS.SPRINKLER_TYPES.PLUG].includes(_get(outlet, 'sprinkler.sprinklerType.id', '')) && (!outlet.sprinkler.nozzle || outlet.sprinkler.nozzle.id === AUDITS.NOZZLE.NULL) ) {
              sprinklersWithoutNozzle.push({
                outletNumber: outlet.number,
                spanNumber: span.number
              })
            }

            if (outlet.sprinkler && ![AUDITS.SPRINKLER_TYPES.WITHOUT_SPRINKLER, AUDITS.SPRINKLER_TYPES.PLUG].includes(_get(outlet, 'sprinkler.sprinklerType.id', '')) && !outlet.sprinkler.pad) {
              sprinklersWithoutPad.push({
                outletNumber: outlet.number,
                spanNumber: span.number
              })
            }
          }
          total++
        })
      }
    })
  }

  return {
    percentage: total > 0 ? (correct / total) * 100 : 0,
    total,
    correct,
    outletsWithoutRegulator: outletsWithoutRegulator.sort((a, b) => a.outletNumber - b.outletNumber),
    sprinklersWithoutNozzle: sprinklersWithoutNozzle.sort((a, b) => a.outletNumber - b.outletNumber),
    sprinklersWithoutPad: sprinklersWithoutPad.sort((a, b) => a.outletNumber - b.outletNumber)
  }
}

export function getOutletsReportData (auditData) {
  // Extract all outlets from spans using lodash
  const allOutlets = _flatMap(_get(auditData, 'spans', []), 'outlets')
    .filter(outlet => outlet.type && outlet.type.id === AUDITS.OUTLET_TYPES.OUTLET.ID) // Filter out plugged outlets

  // Filter outlets with valid regulator data and create regulator keys
  const regulatorKeys = allOutlets
    .filter(outlet => _get(outlet, 'regulator.regulatorType.model'))
    .map(outlet => {
      const brand = _get(outlet, 'regulator.regulatorType.brand', '')
      const model = _get(outlet, 'regulator.regulatorType.model', '')
      const pressureName = _get(outlet, 'regulator.pressure.name', '')
      return `${brand} ${model} ${pressureName} PSI`.trim()
    })
    .filter(key => key) // Remove empty keys

  // Count occurrences using lodash
  const regulatorModels = regulatorKeys.reduce((acc, key) => {
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

  // Filter outlets with valid sprinkler data and create sprinkler keys
  const sprinklerKeys = allOutlets
    .filter(outlet => _get(outlet, 'sprinkler.sprinklerType.model') && ![AUDITS.SPRINKLER_TYPES.WITHOUT_SPRINKLER, AUDITS.SPRINKLER_TYPES.PLUG].includes(_get(outlet, 'sprinkler.sprinklerType.id', '')))
    .map(outlet => {
      const brand = _get(outlet, 'sprinkler.sprinklerType.brand', '')
      const model = _get(outlet, 'sprinkler.sprinklerType.model', '')
      return `${brand} ${model}`.trim()
    })
    .filter(key => key) // Remove empty keys

  // Count occurrences using lodash
  const sprinklerModels = sprinklerKeys.reduce((acc, key) => {
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})


  const sprinklersWithoutPad = allOutlets
    .filter(outlet => outlet.sprinkler && ![AUDITS.SPRINKLER_TYPES.WITHOUT_SPRINKLER, AUDITS.SPRINKLER_TYPES.PLUG].includes(_get(outlet, 'sprinkler.sprinklerType.id', '')) && !outlet.sprinkler.pad)
    .map(outlet => ({
      outletNumber: outlet.number,
      spanNumber: outlet.spanNumber
    }))
    .sort((a, b) => a.outletNumber - b.outletNumber)


  // Filter outlets with valid sprinkler data and pad data and create pad keys
  const padKeys = allOutlets
    .filter(outlet => _get(outlet, 'sprinkler.pad'))
    .map(outlet => {
      const brand = _get(outlet, 'sprinkler.pad.brand', '')
      const model = _get(outlet, 'sprinkler.pad.model', '')
      return `${brand} ${model}`.trim()
    })
    .filter(key => key) // Remove empty keys

  // Count occurrences using lodash
  const padModels = padKeys.reduce((acc, key) => {
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {})

  const outletsWithoutWeight = allOutlets
    .filter(outlet => !outlet.weight)
    .map(outlet => ({
      outletNumber: outlet.number,
      spanNumber: outlet.span.number
    }))
    .sort((a, b) => a.outletNumber - b.outletNumber)

  // Extract all tyre sizes from spans using lodash
  const tyreSizes = _uniq(_flatMap(_get(auditData, 'spans', []), span =>
    _get(span, 'tower.tyres', [])
  )
    .filter(tyre => _get(tyre, 'size.name'))
    .map(tyre => _get(tyre, 'size.name')))

  const maxAccLarge = _max(_get(auditData, 'spans', [])
    .map(span => parseFloat(_get(span, 'calculatedData.accLarge', 0)))
  ) || 0

  const maxEndSystemRadius = _max(_get(auditData, 'endSystems', [])
    .map(endSystem => parseFloat(_get(endSystem, 'radius', 0)))
  ) || 0

  const theoreticalIrrigationTotalLength = maxAccLarge + maxEndSystemRadius

  return {
    regulatorModels,
    sprinklerModels,
    sprinklersWithoutPad,
    padModels,
    outletsWithoutWeight,
    allOutletsCount: allOutlets.length,
    tyreSizes,
    theoreticalIrrigationTotalLength,
    endsOfSystemData: auditData.endSystems.filter(outlet => outlet.outletType && (outlet.outletType.id === AUDITS.OUTLET_TYPES.CANNON.ID) || (outlet.outletType.id === AUDITS.OUTLET_TYPES.END_OF_PIVOT_SPRINKLER.ID))
  }
}

export function formatNumberHelper (num, decimals = 1) {
  // Convert to number if it's a string
  const number = parseFloat(num)

  // Check if it's a valid number
  if (isNaN(number)) {
    return '-'
  }

  // Check if it's an integer (no decimal part)
  if (number % 1 === 0) {
    return number.toString()
  }

  // Otherwise, show with specified decimals
  const truncatedNumber = Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals)

  // Check if all decimals (up to the specified amount) are 0
  // Use a tolerance based on the decimal precision
  const integerPart = Math.floor(truncatedNumber)
  const tolerance = Math.pow(10, -decimals) / 2
  const hasNonZeroDecimals = Math.abs(truncatedNumber - integerPart) >= tolerance

  // If all decimals are 0 (within the specified precision), show as integer
  if (!hasNonZeroDecimals) {
    return integerPart.toString()
  }

  // Otherwise, format with the specified number of decimals
  // Remove trailing zeros
  return truncatedNumber.toFixed(decimals).replace(/\.?0+$/, '')
}

/**
 * Maps target points to their nearest sample points based on geographical distance
 * and assigns elevation data from the nearest sample to each target point.
 *
 * @param {Array<Object>} targetPoints - Array of target points to map elevation data to
 * @param {number} targetPoints[].lat - Latitude of the target point
 * @param {number} targetPoints[].lng - Longitude of the target point
 * @param {Array<Object>} samples - Array of sample points containing elevation data
 * @param {Object} samples[].location - Location object of the sample point
 * @param {number} samples[].location.lat - Latitude of the sample point
 * @param {number} samples[].location.lng - Longitude of the sample point
 * @param {number} samples[].elevation - Elevation value of the sample point
 *
 * @returns {Array<Object>} Array of target points with additional elevation data:
 * @returns {number} returns[].lat - Original latitude of the target point
 * @returns {number} returns[].lng - Original longitude of the target point
 * @returns {number|null} returns[].elevation - Elevation from the nearest sample point, or null if no samples
 * @returns {Object|null} returns[].nearest - The nearest sample point object, or null if no samples
 * @returns {number|null} returns[].distance - Distance to the nearest sample point in meters, or null if no samples
 */
export function mapAltimetryByNearest(targetPoints, samples) {
  if (!Array.isArray(targetPoints) || !Array.isArray(samples)) return []
  if (samples.length === 0) {
    return targetPoints.map(p => ({ ...p, elevation: null, nearest: null, distance: null }))
  }

  const keyOf = (p) => `${p.lat},${p.lng}`
  const resolveOne = (p) => {
    // Find the nearest sample
    const nearest = _minBy(samples, s => getDistance(
      { latitude: p.lat, longitude: p.lng },
      { latitude: s.location.lat, longitude: s.location.lng }
    ))
    const distance = getDistance(
      { latitude: p.lat, longitude: p.lng },
      { latitude: nearest.location.lat, longitude: nearest.location.lng }
    )
    return { ...p, elevation: nearest.elevation, nearest, distance }
  }

  const memoResolveOne = _memoize(resolveOne, keyOf)
  return targetPoints.map(memoResolveOne)
}

export function isObservationsDocumentEmpty (auditData) {
  if (!auditData.observations || !auditData.observations.document || !Array.isArray(auditData.observations.document)) {
    return true
  }

  // Recursive function to find text content in any node
  const hasTextContent = (node) => {
    if (!node) {
      return false
    }

    // Check if node has text property with non-empty content
    if (node.text !== undefined && node.text !== null) {
      const trimmedText = String(node.text).trim()
      if (trimmedText !== '') {
        return true
      }
    }

    // Check if node has props with content (like images)
    if (node.props) {
      // Check for image blocks or other components with content
      if (node.type === 'component-block' && node.props.asset) {
        return true
      }
    }

    // Recursively check children
    if (node.children && Array.isArray(node.children)) {
      return node.children.some(child => hasTextContent(child))
    }

    return false
  }

  // Check all document items for content
  return !auditData.observations.document.some(item => {
    // Skip empty paragraphs
    if (item.type === 'paragraph') {
      return hasTextContent(item)
    }
    // Check other types (lists, component-blocks, etc.)
    return hasTextContent(item)
  })
}

export function parseCrop (cropId, cropData) {
  const startDate = cropData.startDate
    ? (typeof cropData.startDate === 'string'
      ? new Date(cropData.startDate)
      : cropData.startDate.toDate())
    : undefined

  const cropDataObj = {
    ...cropData,
    id: cropId,
    field: cropData.field ? cropData.field.id : null,
    devices: cropData.devices ? cropData.devices.map(device => device.id) : [],
    type: cropData.type ? cropData.type.split('|') : [],
    sowingDate: startDate
      ? `${startDate.getDate().toString().padStart(2, '0')}/${(startDate.getMonth() + 1).toString().padStart(2, '0')}/${startDate.getFullYear()}`
      : '',
    campaign: cropData.campaign ? cropData.campaign.id : null,
    organization: cropData.organization ? cropData.organization.id : null,
    clonedFrom: cropData.clonedFrom ? cropData.clonedFrom.map(cloned => cloned.id) : []
  }

  if (cropDataObj.location) {
    cropDataObj.location = parseFirestoreLocation(cropDataObj.location)
  }
  if (cropDataObj.startPoint) {
    cropDataObj.startPoint = parseFirestoreLocation(cropDataObj.startPoint)
  }
  if (cropDataObj.endPoint) {
    cropDataObj.endPoint = parseFirestoreLocation(cropDataObj.endPoint)
  }
  if (cropDataObj.points) {
    cropDataObj.points = cropDataObj.points.map(p => {
      if (!p.coordinatesString) {
        p.coordinatesString = `${p.latitude}, ${p.longitude}`
      }
      return p
    })
  }

  return cropDataObj
}

export function parseStringToCoordinates (input) {
  if (typeof input !== 'string') {
    return { valid: false, latitude: null, longitude: null }
  }

  const coords = isLikelyDMS(input)
    ? parseDMSLatLonString(input)
    : parseDecimalLatLonString(input)


  return coords && isValidCoordinate(coords) ? coords : null
}

/**
 * Returns only the fields that changed between two objects (recursive for nested objects and arrays)
 * Preserves the original object structure using lodash set
 * @param {Object} oldData - Previous data
 * @param {Object} newData - New data
 * @param {string} prefix - Prefix for nested keys (used internally for recursion)
 * @returns {Object} - Changed fields with new values (preserving object structure)
 */
export function getDataChanges (oldData = {}, newData = {}, prefix = '') {
  const changes = {}

  // Check new/modified fields
  Object.keys(newData).forEach(key => {
    const oldValue = oldData[key]
    const newValue = newData[key]
    const fullKey = prefix ? `${prefix}.${key}` : key

    // Skip if values are equal
    if (areValuesEqual(oldValue, newValue)) return

    // If both are arrays, compare element by element
    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
      // If lengths differ, store the whole array
      if (oldValue.length !== newValue.length) {
        _set(changes, fullKey, newValue)
      } else {
        // Compare each element
        newValue.forEach((item, index) => {
          if (!areValuesEqual(oldValue[index], item)) {
            // If both elements are plain objects, recurse
            if (_isPlainObject(oldValue[index]) && _isPlainObject(item)) {
              const nestedChanges = getDataChanges(oldValue[index], item, `${fullKey}[${index}]`)
              // Merge nested changes with changes
              _merge(changes, nestedChanges)
            } else {
              _set(changes, `${fullKey}[${index}]`, item)
            }
          }
        })

        // Ensure there is not undefined array items
        const arrayChanges = _get(changes, fullKey)
        if (arrayChanges) {
          _set(changes, fullKey, [...arrayChanges].map(item => item === undefined ? null : item))
        }
      }
    } else if (_isPlainObject(oldValue) && _isPlainObject(newValue)) {
      // If both are plain objects, recurse
      const nestedChanges = getDataChanges(oldValue, newValue, fullKey)
      // Merge nested changes with changes
      _merge(changes, nestedChanges)
    } else {
      // Different values or different types
      _set(changes, fullKey, newValue)
    }
  })

  return changes
}

/**
 * Compares two values handling Firestore-specific types
 * @param {*} v1 - First value
 * @param {*} v2 - Second value
 * @returns {boolean} - True if equal
 */
function areValuesEqual (v1, v2) {
  const getTypeName = (val) => val && val.constructor && val.constructor.name
  // Handle Firestore DocumentReference
  if (getTypeName(v1) === 'DocumentReference' && getTypeName(v2) === 'DocumentReference') {
    return v1.path === v2.path
  }
  // Handle Firestore Timestamp
  if (getTypeName(v1) === 'Timestamp' && getTypeName(v2) === 'Timestamp') {
    return v1.getDate().getTime() === v2.getDate().getTime()
  }
  // Handle Date objects
  if (v1 instanceof Date && v2 instanceof Date) {
    return v1.getTime() === v2.getTime()
  }
  // Use lodash isEqual for everything else
  return _isEqual(v1, v2)
}



// WEBPACK FOOTER //
// ./src/lib/helpers.js